import { createRef, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import Form from '@rjsf/core';
import { omit, isEmpty, isObject, uniq } from 'lodash';
import { nanoid } from 'nanoid';
import styled from '@emotion/styled';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useJobBulkEdit } from '@/components/OrderPlacement/OPReviewOrder/useJobBulkEdit.hook';
import { addVersionKeys, setDraftResumed, setPrimaryVersionIds, setVersionSelections } from '@/redux/slices/orderPlacementSlice';
import { selectWorkflows } from '@/redux/selectors/profileSelectors';
import { getMultipleProfiles } from '@/redux/slices/profileSlice';
import {
  selectDraftState,
  selectIsResumedDraft,
  selectOutputVersions,
  selectPrimaryVersionIds,
  selectVersions
} from '@/redux/selectors/orderPlacementSelectors';
import { axios, getLineageMetaTitle, isArrayTheSame } from '@/_utils/utils';
import { findandFormatUTCDateInString } from '@/_utils/date';
import { useToast } from '@/components/_common/Toast';
import PolarisIcon from '@/components/_common/PolarisIcon';
import TableNoResults from '@/components/_common/Table/TableNoResults';
import JobFilterChip from '@/components/OrderPlacement/OPReviewOrder/JobFilterChip';
import OPOrderStepperButtons from '@/components/OrderPlacement/OPOrderStepperButtons';
import OPJobAccordion from '@/components/OrderPlacement/OPJobAccordion/OPJobAccordion';
import OPOrderDetails from '@/components/OrderPlacement/OPStep1OrderDetails/OPOrderDetails';
import OPOutputVersions from '@/components/OrderPlacement/OPStep2FileSelection/OutputVersions/OPOutputVersions';
import OPJobBulkEditModal from '@/components/OrderPlacement/OPJobBulkEditModal';
import { selectAllTitleMetadata } from '@/redux/selectors/titleV2Selectors';
import { fetchTitleMetadata } from '@/redux/slices/titleV2Slice';
import { CustomerProfile, FormValues, JobDraft } from '@/redux/slices/slice_types';

// generate unique jobs by combining profiles and versions
// 3 profiles, 2 versions = 6 jobs

const findSingleKeyValue = (obj: Record<string, any>, key: string): any => {
  if (!isObject(obj)) return null;

  for (const k in obj) {
    const v = obj[k];

    if (k === key) return v;

    if (isObject(v)) {
      const found = findSingleKeyValue(v, key);
      if (found) return found;
    }
  }

  return null;
};

const OPReviewOrder = () => {
  const { info } = useToast();
  const { values } = useFormikContext<FormValues>();
  const {
    getLineageMetaTitle: getLineageMetaTitleContext,
    displayTitleMetadataByhierarchy,
    disableBulkEdit
  } = useContext(ConfigContext)?.orderPlacementConfig || {};
  const dispatch = useAppDispatch();
  const selectedOutputVersions = useAppSelector(selectOutputVersions);
  const selectedVersions = useAppSelector(selectVersions);
  const draftState = useAppSelector(selectDraftState);
  const isResumedDraft = useAppSelector(selectIsResumedDraft);
  const workflows = useAppSelector(selectWorkflows);
  const allMetadata = useAppSelector(selectAllTitleMetadata);
  const primaryVersionIds = useAppSelector(selectPrimaryVersionIds);

  const [jobs, setJobs] = useState<Record<string, JobDraft>>({});
  const hierarchy = findSingleKeyValue(jobs, 'hierarchy');
  const [titleOptions, setTitleOptions] = useState<string[]>([]);
  const [customerOptions, setCustomerOptions] = useState<JobDraft['customer'][]>([]);
  const [profileOptions, setProfileOptions] = useState<CustomerProfile['name'][]>([]);
  const [selectedTitleOptions, setSelectedTitleOptions] = useState<string[]>([]);
  const [selectedCustomerOptions, setSelectedCustomerOptions] = useState<JobDraft['customer'][]>([]);
  const [selectedProfileOptions, setSelectedProfileOptions] = useState<CustomerProfile['name'][]>([]);

  const [haveJobsBeenEdited, setHaveJobsBeenEdited] = useState(false);
  const [sort, setSort] = useState('');
  const customersRef = useRef<string[]>([]);
  const schemaRefs = useRef<Record<string, RefObject<Form>>>({});
  const hasGeneratedJobs = useRef(false);

  const jobsArray = useMemo(() => Object.keys(jobs).map((key) => jobs[key]), [jobs]);
  const filteredJobs = useMemo(
    () =>
      jobsArray
        .filter((job) => {
          if (selectedProfileOptions.length) {
            const profileFound = !!job.profile?.name && selectedProfileOptions.includes(job.profile.name);
            if (!profileFound) return false;
          }

          if (selectedCustomerOptions.length) {
            const customerFound = selectedCustomerOptions.includes(job.customer);
            if (!customerFound) return false;
          }

          if (selectedTitleOptions.length) {
            const titleFound = selectedTitleOptions.includes(
              getLineageMetaTitleContext ? getLineageMetaTitleContext(job.version) : getLineageMetaTitle(job.version)
            );
            if (!titleFound) return false;
          }

          return true;
        })
        .sort((a, b) => {
          switch (sort) {
            case 'due_date':
              return new Date(a.due_date ?? 0).getTime() - new Date(b.due_date ?? 0).getTime();
            case 'title': {
              const titleNameA = getLineageMetaTitleContext ? getLineageMetaTitleContext(a.version) : getLineageMetaTitle(a.version);
              const titleNameB = getLineageMetaTitleContext ? getLineageMetaTitleContext(b.version) : getLineageMetaTitle(b.version);
              return titleNameA.localeCompare(titleNameB);
            }
            case 'profile':
              return b.profile?.name
                ? a.profile?.name.localeCompare(b.profile.name)
                : 1 /* List jobs without a profile selected first (when sorting by profile) */;
            case 'customer':
              return a.customer.localeCompare(b.customer);
          }
        }),
    [jobsArray, selectedProfileOptions, selectedCustomerOptions, selectedTitleOptions, sort]
  );
  const hasFilters = selectedProfileOptions.length > 0 || selectedCustomerOptions.length > 0 || selectedTitleOptions.length > 0;
  const hasJobs = Object.keys(jobs).length > 0;
  const isSimpleOrder = !selectedOutputVersions?.length;
  const jobCount = isSimpleOrder ? filteredJobs.length : selectedOutputVersions?.length;

  const clearFilters = () => {
    setSelectedProfileOptions([]);
    setSelectedCustomerOptions([]);
    setSelectedTitleOptions([]);
  };

  const handleUpdateJob = useCallback((updatedJob: JobDraft) => {
    setJobs((prevJobs) => ({ ...prevJobs, [updatedJob.id]: updatedJob }));
    schemaRefs.current = {
      ...schemaRefs.current,
      [updatedJob.id]: createRef()
    };
  }, []);

  const {
    isBulkEditModalOpen,
    selectedJobIds,
    selectedJobs,
    isNoJobsSelected,
    isSomeJobsSelected,
    isAllJobsSelected,
    removeJobFromSelectedJobs,
    handleSelectAllCheckboxChange,
    handleBulkEditButtonClick,
    handleJobSelectionChange,
    handleBulkEditModalClose,
    onBulkEditApply
  } = useJobBulkEdit({
    jobs,
    filteredJobs,
    selectedProfileOptions,
    selectedCustomerOptions,
    selectedTitleOptions,
    handleUpdateJob,
    setHaveJobsBeenEdited
  });

  const handleDeleteJob = useCallback(
    (deletedJob: JobDraft) => {
      const undoBtn = () => <Button onClick={() => setJobs(jobs)}>Undo</Button>;
      const newJobs = omit(jobs, deletedJob.id);
      delete schemaRefs.current[deletedJob.id];
      setJobs(newJobs);
      removeJobFromSelectedJobs(deletedJob.id.toString());
      info('The job has been deleted successfully.', { action: undoBtn });
    },
    [jobs]
  );

  const handleSortChange = (e: SelectChangeEvent) => {
    let newSort = e.target.value;
    if (newSort === 'clear') newSort = '';
    setSort(newSort);
  };

  useEffect(() => {
    return () => {
      dispatch(setDraftResumed(false));
    };
  }, []);

  // generate an individual job by combining profile with selected version
  // currently only for simple flow
  useEffect(() => {
    const profiles = values.jobs || [];
    const generatedJobs = isResumedDraft && draftState.jobs ? JSON.parse(JSON.stringify(draftState.jobs)) : {};
    if (isResumedDraft && draftState.jobs) {
      if (selectedVersions.length === 0) {
        const versionPromises = uniq(primaryVersionIds).map((id) =>
          axios
            .get(`/inventory/titles/${id}/`)
            .then((res) => res.data?.data)
            .catch(() => null)
        );

        Promise.all(versionPromises).then((versionDataArray) => {
          const validVersions = versionDataArray.filter(Boolean);
          if (validVersions.length > 0) {
            dispatch(addVersionKeys(validVersions));
            validVersions.forEach((versionData) => {
              dispatch(setVersionSelections(versionData));
            });
            dispatch(setPrimaryVersionIds([]));
          }
        });
        return;
      }
      Object.keys(generatedJobs).forEach((gId) => {
        const version = generatedJobs[gId].version || selectedVersions.find((v) => v.id == generatedJobs[gId].primary_version_id);
        generatedJobs[gId] = {
          ...generatedJobs[gId],
          version
        };
      });
    } else {
      if (!profiles.length) return;
      profiles.forEach((profile) => {
        const profileData = profile.profile?.data;
        const { workflow } = profileData;
        const templateInputs = profileData?.template_inputs;
        const defaultInstanceValues: Record<string, any> = {};
        if (templateInputs) {
          const instanceSchema = workflow && workflows?.[workflow]?.instance_schema?.properties;
          if (instanceSchema) {
            Object.keys(instanceSchema).forEach((key) => {
              if (templateInputs[key]) defaultInstanceValues[key] = templateInputs[key];
            });
          }
        }
        const orderSpecInputs = profileData?.order_spec_inputs;
        if (!profile?.customer?.length || !profile?.profile?.name?.length) return;
        // generate a unique ID so we can keep track of jobs and easily update them
        selectedVersions.forEach((version) => {
          const id = nanoid();
          generatedJobs[id] = {
            ...profile,
            version,
            id,
            instance_inputs: defaultInstanceValues,
            order_spec_inputs: orderSpecInputs,
            workflow_version: (workflow && workflows?.[workflow]?.workflow_version) || undefined
          };
        });
      });
    }
    const generatedJobsArray = Object.keys(generatedJobs).map((key) => generatedJobs[key]);
    customersRef.current = [...new Set(generatedJobsArray.filter((job) => job.customer).map((job) => job.customer))];
    setJobs(generatedJobs);
    setTitleOptions([
      ...new Set(
        generatedJobsArray.map((job) => (getLineageMetaTitleContext ? getLineageMetaTitleContext(job.version) : getLineageMetaTitle(job.version)))
      )
    ]);
    setCustomerOptions([...new Set(generatedJobsArray.map((job) => job.customer))]);
    setProfileOptions([
      ...new Set(
        generatedJobsArray.flatMap((job) => {
          const name = findandFormatUTCDateInString(job.profile?.name);
          return name ? [name] : [];
        })
      )
    ]);
  }, [selectedVersions]);

  useEffect(() => {
    // update the filter options when jobs are updated
    if (Object.keys(jobs).length > 0) {
      if (hasGeneratedJobs.current === false) hasGeneratedJobs.current = true;
      setTitleOptions([
        ...new Set(jobsArray.map((job) => (getLineageMetaTitleContext ? getLineageMetaTitleContext(job.version) : getLineageMetaTitle(job.version))))
      ]);
      const customers = [...new Set(jobsArray.filter((job) => job.customer).map((job) => job.customer))];
      setCustomerOptions(customers);

      const profiles = jobsArray.flatMap((job) => {
        const name = findandFormatUTCDateInString(job.profile?.name);
        return name ? [name] : [];
      });
      setProfileOptions([...new Set(profiles)]);

      // compare if old and new customers are NOT the same, before fetching request
      // this is to avoid unnecessary API calls when updating a single job in the form
      if (customers.length > 0 && !isArrayTheSame(customers, customersRef.current)) {
        dispatch(getMultipleProfiles(customers));
      }

      customersRef.current = customers;
    }

    // create a ref for each job that has a schema
    // so that we can use it for programmatic validation
    const parentIds: number[] = [];

    const jobsWithSchema = Object.keys(jobs).filter((key) => {
      if (displayTitleMetadataByhierarchy && isEmpty(allMetadata) && jobs[key]?.version?.parent) {
        parentIds.push(jobs[key]?.version?.parent);
      }
      const workflow = jobs[key].profile?.data?.workflow;
      const instanceSchema = workflow && workflows?.[workflow]?.instance_schema;

      return workflow && instanceSchema?.properties && Object.keys(instanceSchema.properties).length > 0;
    });

    jobsWithSchema.forEach((jobId) => {
      schemaRefs.current = {
        ...schemaRefs.current,
        [jobId]: createRef()
      };
    });

    // make metadata request if metadata doesnt have any parent ids saved
    if (displayTitleMetadataByhierarchy && isEmpty(allMetadata) && parentIds.length > 0) {
      dispatch(
        fetchTitleMetadata({
          body: {
            language: 'en-US',
            hierarchy: hierarchy,
            entity_ids: parentIds
          }
        })
      );
    }
  }, [jobs]);

  return (
    <>
      <Typography variant="h4" marginBottom={3}>
        Review and finalize your order
      </Typography>

      <Card sx={{ p: 3 }}>
        <CardHeader title="Order Details" />
        <CardContent>
          <OPOrderDetails />
        </CardContent>
        <Divider sx={{ mt: 3, mb: 2 }} />
        <Grid container alignItems="center" flexWrap="nowrap" marginTop={4} paddingLeft={3}>
          {hasJobs || !selectedVersions?.length ? (
            <JobCount>{!hasFilters ? `${jobCount} jobs` : `${jobCount} of ${Object.keys(jobs).length} jobs found`}</JobCount>
          ) : hasGeneratedJobs.current ? (
            <Alert severity="warning" sx={{ my: 3 }}>
              <AlertTitle sx={{ fontWeight: 'bold' }}>No jobs found</AlertTitle>
              At least one job is required to submit an order.
            </Alert>
          ) : null}
        </Grid>
        <Grid container alignItems="center" flexWrap="nowrap" marginTop={1} id="datepicker-popper-id">
          {!!selectedVersions?.length && hasJobs && (
            <Grid alignItems="center" item container marginLeft={3} paddingRight={2.5}>
              {jobsArray.length > 1 && !disableBulkEdit && (
                <Tooltip title={isAllJobsSelected ? 'Deselect all' : 'Select all'}>
                  <Checkbox checked={isAllJobsSelected} indeterminate={isSomeJobsSelected} onChange={handleSelectAllCheckboxChange} />
                </Tooltip>
              )}
              <JobFilterChip
                key="profile-filter-chip"
                label="Profile"
                filterOptions={profileOptions}
                selectedOptions={selectedProfileOptions}
                onChange={(newSelectedOptions) => setSelectedProfileOptions(newSelectedOptions)}
              />
              <JobFilterChip
                key="customer-filter-chip"
                label="Customer"
                filterOptions={customerOptions}
                selectedOptions={selectedCustomerOptions}
                onChange={(newSelectedOptions) => setSelectedCustomerOptions(newSelectedOptions)}
              />
              <JobFilterChip
                key="title-filter-chip"
                label="Title"
                filterOptions={titleOptions}
                selectedOptions={selectedTitleOptions}
                onChange={(newSelectedOptions) => setSelectedTitleOptions(newSelectedOptions)}
              />
              <FormControl size="small" sx={{ minWidth: 150, ml: 1 }}>
                <InputLabel>Sort By</InputLabel>
                <Select value={sort} label="Sort By" onChange={handleSortChange}>
                  <MenuItem value="clear" sx={(theme) => ({ color: theme.palette.action.disabled })}>
                    -
                  </MenuItem>
                  <MenuItem value="due_date">Due date</MenuItem>
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="profile">Profile</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                </Select>
              </FormControl>
              {jobsArray.length > 1 && !disableBulkEdit && (
                <Tooltip title={isNoJobsSelected ? 'Select at least one job' : null}>
                  <Box sx={{ ml: 3 }}>
                    <Button disabled={isNoJobsSelected} variant="contained" onClick={handleBulkEditButtonClick}>
                      Bulk Edit
                    </Button>
                  </Box>
                </Tooltip>
              )}
              {hasFilters && (
                <FilterActions>
                  <StyledClearFiltersButton
                    data-testid="clear-filters-btn"
                    color="primary"
                    size="small"
                    variant="text"
                    onClick={clearFilters}
                    endIcon={<PolarisIcon icon="close" />}
                  >
                    Clear All Filters
                  </StyledClearFiltersButton>
                </FilterActions>
              )}
            </Grid>
          )}
        </Grid>
        {hasJobs || selectedOutputVersions?.length ? (
          <CardContent>
            {selectedOutputVersions?.length ? (
              <Grid container justifyContent="space-between" alignItems="stretch">
                <OPOutputVersions isReview />
              </Grid>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {filteredJobs.map((job) => (
                  <OPJobAccordion
                    key={job.id}
                    job={job}
                    hideSelectAndDelete={jobsArray.length === 1}
                    isSelected={selectedJobIds[job.id]}
                    onUpdateJob={handleUpdateJob}
                    onDeleteJob={handleDeleteJob}
                    onSelectedChange={handleJobSelectionChange(job.id.toString())}
                    setHaveJobsBeenEdited={setHaveJobsBeenEdited}
                    schemaRef={schemaRefs.current?.[job.id]}
                  />
                ))}
              </div>
            )}
            {selectedJobs.length > 0 && (
              <OPJobBulkEditModal
                isOpen={isBulkEditModalOpen}
                jobs={selectedJobs}
                totalJobsCount={jobsArray.length}
                onClose={handleBulkEditModalClose}
                onApply={onBulkEditApply}
              />
            )}
          </CardContent>
        ) : hasFilters ? (
          <TableNoResults isClearable handleClearFilters={clearFilters} maxHeight={300} sx={{ mb: 4 }} />
        ) : (
          <Stack direction="column" spacing={1}>
            <Skele /> <Skele /> <Skele />
            <Skele /> <Skele /> <Skele />
          </Stack>
        )}
      </Card>

      <Grid container>
        <Grid item marginLeft="auto" marginTop={5}>
          <OPOrderStepperButtons onClearFilters={clearFilters} jobs={jobs} haveJobsBeenEdited={haveJobsBeenEdited} schemaRefs={schemaRefs} />
        </Grid>
      </Grid>
    </>
  );
};

export default OPReviewOrder;

const Skele = styled(Skeleton)`
  margin: 0 ${({ theme }) => theme.spacing(4)}!important;
  height: 60px;
`;

const FilterActions = styled.div`
  margin-left: auto;
`;

const StyledClearFiltersButton = styled(Button)`
  margin-left: ${(props) => props.theme.spacing(1)};
  margin-right: ${(props) => props.theme.spacing(1)};
  height: 30px;
`;

const JobCount = styled.div`
  color: ${({ theme }) => theme.palette.default.main};
  font-size: 1.2rem;
  padding-left: ${(props) => props.theme.spacing(1)};
  margin-bottom: ${(props) => props.theme.spacing(1)};
  white-space: nowrap;
`;
